import { useState } from "react";
import { Alert, Button, Modal, Spinner, Table } from "react-bootstrap";
import { DropdownSelect } from "components/DropdownSelect";
import { useEmpresas } from "hooks/useEmpresas";
import { useConfiguracaoCrlv } from "../../hooks/useConfiguracaoCrlv";

interface Props {
  show: boolean;
  onHide: () => void;
}

export function ModalConfiguracaoCrlv({ show, onHide }: Props) {
  const { registros, loading, adicionar, remover } = useConfiguracaoCrlv(show);
  const { empresasOptions, empresas } = useEmpresas();
  const [idEmpresa, setIdEmpresa] = useState<number | "">("");
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const handleAdicionar = async () => {
    if (!idEmpresa) return;
    const empresa = empresas.find((e) => e.id === idEmpresa);
    if (!empresa) return;
    setSalvando(true);
    setErro(null);
    try {
      await adicionar(String(idEmpresa), empresa.razao_social);
      setIdEmpresa("");
    } catch (e: any) {
      setErro(e?.response?.data?.message ?? e?.message ?? "Erro ao adicionar");
    } finally {
      setSalvando(false);
    }
  };

  const handleRemover = async (clientId: string) => {
    setErro(null);
    try {
      await remover(clientId);
    } catch (e: any) {
      setErro(e?.response?.data?.message ?? e?.message ?? "Erro ao remover");
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Configuração de Entrega do CRLV</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Empresas nesta lista <strong>não receberão o CRLV</strong>. Se a empresa não estiver na
          lista, o CRLV é entregue normalmente.
        </p>

        {erro && (
          <Alert variant="danger" dismissible onClose={() => setErro(null)}>
            {erro}
          </Alert>
        )}

        <div className="d-flex gap-2 mb-3">
          <div style={{ flex: 1 }}>
            <DropdownSelect
              options={empresasOptions}
              title="Selecione a empresa"
              searchable
              selectedValue={idEmpresa}
              onSelect={(v) => setIdEmpresa(v === "" ? "" : Number(v))}
            />
          </div>
          <Button
            variant="primary"
            onClick={handleAdicionar}
            disabled={salvando || !idEmpresa}
          >
            {salvando ? <Spinner animation="border" size="sm" /> : "Adicionar"}
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-3">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : (
          <Table bordered hover size="sm">
            <thead>
              <tr>
                <th>Empresa</th>
                <th>Criado em</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {registros.length === 0 ? (
                <tr>
                  <td colSpan={3} className="text-center text-muted">
                    Nenhuma restrição cadastrada
                  </td>
                </tr>
              ) : (
                registros.map((r) => (
                  <tr key={r.clientId}>
                    <td>{r.nomeCliente ?? r.clientId}</td>
                    <td>{new Date(r.criadoEm).toLocaleDateString("pt-BR")}</td>
                    <td>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleRemover(r.clientId)}
                      >
                        Remover
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Fechar
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
